import { DeleteOutlined } from "@ant-design/icons";
import { Button, Comment, Empty, Form, Input, List, Popconfirm, Tooltip, Typography } from "antd";
import moment from "moment";
import React, { useRef, useState } from "react";

import VideoCommentHooks from "@Components/activity/VideoCommentHooks";
import { IDispatchSetStateAction } from "@Components/shared/types";
import UserIcon from "@Components/user/UserIcon";
import { UserContext } from "@Constants";
import SessionManager from "@Core/session";
import { ICommentDetails } from "@Models/IActivityDetails";
import { formatDuration, hasCapability } from "@Utils";

import "@Styles/pages/activityPage.scss";
import IconButton from "@Components/shared/IconButton";

interface IProps {
    progress: number;
    duration: number;
    activityInstanceId: string;
    comments: ICommentDetails[];
    setComments: IDispatchSetStateAction<ICommentDetails[]>;
    onCommentsChanged: () => Promise<void>;
}

const CommentItem = ({ item, listElement, props }) => {
    const { onEditPreviousComment, onDeletePreviousComment } = VideoCommentHooks(listElement, props);
    const [isEditing, setIsEditing] = useState(false);
    const { Paragraph } = Typography;
    return (
        <Comment
            className="video-feedback-comment"
            actions={[
                hasCapability("Comment/Remove", UserContext, item.activityCommentId) && (
                    <Popconfirm
                        title="Are you sure you want to delete this comment?"
                        onConfirm={(e) => onDeletePreviousComment(item)}
                        onCancel={null}
                        okText="Yes"
                        cancelText="No"
                        cancelButtonProps={{ className: "secondary" }}
                    >
                        <IconButton className="secondary" title="Delete" size={"small"} icon={<DeleteOutlined />} />
                    </Popconfirm>
                ),
            ]}
            author={formatDuration(item.time * 0.001) + " - " + item.fullName}
            avatar={<UserIcon fullName={item.fullName} userId={item.userId} size={40} />}
            content={
                <Paragraph
                    editable={{
                        onChange: (value) => {
                            onEditPreviousComment(value, item);
                            setIsEditing(false);
                        },
                        onStart: () => setIsEditing(true),
                    }}
                    style={{ marginLeft: isEditing ? 10 : 0 }}
                >
                    {item.content}
                </Paragraph>
            }
            datetime={
                <Tooltip title={moment(item.dateTime).fromNow()}>
                    <span>{moment(item.dateTime).fromNow()}</span>
                </Tooltip>
            }
        />
    );
};

const VideoComment: React.FC<IProps> = (props: IProps) => {
    const listElement = useRef<HTMLDivElement>(null);
    const { commentSubmissionState, onChange, onSubmitNewComment } = VideoCommentHooks(listElement, props);

    const { TextArea } = Input;
    const { comments } = props;

    const sort = (data: ICommentDetails[]) => {
        return data.slice().sort((a, b) => (a.time > b.time ? 1 : b.time > a.time ? -1 : 0));
    };

    const limit = (data: ICommentDetails[]) => {
        if (data.length === 0) return [];
        return data.filter((item) => item.time <= props.duration * props.progress * 10);
    };

    return (
        <div className={"video-comment-list-container"}>
            <div className={"video-comment-list-wrapper"}>
                <div ref={listElement} className={"video-comment-list-overflow"}>
                    <List
                        locale={{
                            emptyText: <Empty description={"No comments."} image={Empty.PRESENTED_IMAGE_SIMPLE} />,
                        }}
                        dataSource={limit(sort(comments))}
                        header={
                            comments.length > 0 &&
                            `${comments.length} ${comments.length === 1 ? "comment" : "comments"}`
                        }
                        itemLayout="vertical"
                        renderItem={(item) => <CommentItem item={item} listElement={listElement} props={props} />}
                    />
                </div>
            </div>
            <Comment
                avatar={<UserIcon fullName={SessionManager.getServiceUser()?.fullName} size={40} />}
                content={
                    <Form>
                        <Form.Item>
                            <TextArea
                                disabled={commentSubmissionState.submitting}
                                rows={3}
                                onChange={onChange}
                                value={commentSubmissionState.value}
                            />
                        </Form.Item>
                        <Form.Item className="video-comment-add-action">
                            <Button
                                htmlType="submit"
                                loading={commentSubmissionState.submitting}
                                onClick={onSubmitNewComment}
                                className="secondary"
                            >
                                Add Comment
                            </Button>
                        </Form.Item>
                    </Form>
                }
            />
        </div>
    );
};
export default VideoComment;
